import { styled, useTheme } from '@chronosphereio/chrono-ui';
import { useMemo } from 'react';
import { ContentDimensions, DEFAULT_LEGEND, DEFAULT_PANEL_LAYOUT_STRATEGY } from '@/core';
import { useIsOnScreen } from '../../plugin-system/runtime/in-view';
import { Legend } from '../Legend';
import { ResponsiveWrapper } from '../ResponsiveWrapper';
import { ContentWithLegendToolbar } from './ContentWithLegendToolbar';
import {
  ContentWithLegendProps,
  ContentWithLegendToggleModeProps,
  getContentWithLegendLayout,
} from './model/content-with-legend-model';
import { useCollapsibleLegend } from './useCollapsibleLegend';

/**
 * Component to help lay out content alongside a `Legend` component based on the
 * configuration of the legend.
 *
 * See the documentation for the `Legend` component for more details about the
 * features and configuration of the legend.
 */
export function ContentWithLegend({
  children,
  legendProps,
  quickFilterProps,
  toggleModeProps: initToggleModeProps,
  width,
  height,
  spacing = 0,
  legendSize = DEFAULT_LEGEND.size,
  minChildrenWidth = 100,
  minChildrenHeight = 100,
  totalLegendItems,
  enableConsistentLegendSize = false,
  layoutStrategy = DEFAULT_PANEL_LAYOUT_STRATEGY,
  collapsibleLegendProps: initCollapsibleLegendProps,
  groupByProps,
}: ContentWithLegendProps) {
  const { theme } = useTheme();

  // Used to unmount the legend when it is offscreen to improve number of elements in the DOM
  // and to prevent issues with scrolling focus behavior (sc-81889)
  const onScreenCtx = useIsOnScreen();
  const isOnScreen = onScreenCtx === undefined ? true : onScreenCtx.isInView;

  const collapsibleLegendProps = useCollapsibleLegend(initCollapsibleLegendProps);

  const { component, content, legend, toolbar, margin, flexDirection, isCompactListLegend } = useMemo(() => {
    return getContentWithLegendLayout({
      width,
      height,
      legendProps,
      minChildrenHeight,
      minChildrenWidth,
      spacing,
      theme,
      legendSize,
      showQuickFilter: quickFilterProps?.show,
      showToggleMode: initToggleModeProps?.show,
      totalLegendItems,
      enableConsistentLegendSize,
      layoutStrategy,
      showCollapsibleLegend: collapsibleLegendProps.show,
      isCollapsed: collapsibleLegendProps.isCollapsed,
      showGroupBy: groupByProps?.show,
    });
  }, [
    collapsibleLegendProps.isCollapsed,
    collapsibleLegendProps.show,
    enableConsistentLegendSize,
    groupByProps?.show,
    height,
    initToggleModeProps?.show,
    layoutStrategy,
    legendProps,
    legendSize,
    minChildrenHeight,
    minChildrenWidth,
    quickFilterProps?.show,
    spacing,
    theme,
    totalLegendItems,
    width,
  ]);

  const toggleModeProps: ContentWithLegendToggleModeProps = useMemo(() => {
    return {
      ...initToggleModeProps,
      // Initialize with legend mode that has been normalized to fallback to a default if not set.
      mode: legend.mode,
    };
  }, [legend.mode, initToggleModeProps]);

  // Unmount Table legend when it is offscreen since it impacts performance more than the List legend.
  const isLegendMounted = isOnScreen || legend.mode === 'List';

  return (
    <div
      style={{
        width: component.width,
        height: component.height,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: flexDirection,
      }}
    >
      <div
        style={{
          width: content.width,
          height: content.height,
          marginRight: `${margin.right}px`,
          marginBottom: `${margin.bottom}px`,
          overflow: 'hidden',

          // When rendering a compact legend, we use flexbox on the content, so that it can
          // grow if the legend does not need the full height. We cannot easily predict this
          // programmatically in the "compact" use case because items with varying widths
          // are rendered inline.
          flex: isCompactListLegend ? 1 : undefined,
        }}
      >
        <ContentWithLegendContent
          isCompactListLegend={isCompactListLegend}
          width={content.width}
          height={content.height}
        >
          {children}
        </ContentWithLegendContent>
      </div>
      {toolbar.show && (
        <ContentWithLegendToolbar
          width={toolbar.width}
          height={toolbar.height}
          quickFilterProps={quickFilterProps}
          toggleModeProps={toggleModeProps}
          collapsibleLegendProps={collapsibleLegendProps}
          groupByProps={groupByProps}
        />
      )}
      {legendProps && legend.show && isLegendMounted && (
        <Legend
          {...legendProps}
          disableCompactLegend={!isCompactListLegend}
          height={legend.height}
          width={legend.width}
        />
      )}
    </div>
  );
}

interface ContentWithLegendContentProps extends Pick<ContentWithLegendProps, 'children'>, ContentDimensions {
  isCompactListLegend: boolean;
}

function ContentWithLegendContent({ isCompactListLegend, children, height, width }: ContentWithLegendContentProps) {
  // When rendering the compact legend, the content is inlined (instead of listed one item per row) making
  // it difficult to perfectly calculate the space needed. To work around this, we use a combination of
  // flexbox, setting the max height based on the calculations for rows and responsive wrapper to ensure
  // that we do not waste extra whitespace when the inlined items can fit in a smaller height than calculated
  // because of inlined layout.
  if (isCompactListLegend) {
    return (
      // This non-flex box is needed between the flexbox parent and the `ResponsiveWrapper` because
      // autosizer and flexbox do not play well together.
      // https://github.com/bvaughn/react-virtualized-auto-sizer?tab=readme-ov-file#can-i-use-this-component-with-flexbox
      <CompactWrapper>
        <ResponsiveWrapper>
          {({ width, height }) => {
            return typeof children === 'function' ? children({ width, height }) : children;
          }}
        </ResponsiveWrapper>
      </CompactWrapper>
    );
  }

  // We rely solely on our calculations for non-compact legend layouts because we can reliably determine the
  // heights with simple math when we render one item per row.
  return typeof children === 'function' ? children({ width, height }) : children;
}

const CompactWrapper = styled('div')(() => {
  return {
    width: '100%',
    height: '100%',
  };
});
